<template>
	<Dialog v-model:visible="visible" :modal="true" :closable="false" :draggable="false" :style="{ width: 'auto' }">
		<template #header>
			<div class="flex items-center gap-2">
				<i class="pi pi-lock text-xl"></i>
				<span class="font-semibold text-xl">Session Expired</span>
			</div>
		</template>

		<div class="flex flex-col gap-3">
			<div class="text-color-secondary">Your session has expired. Please re-authenticate to continue without losing your work.</div>

			<div v-if="loading" class="flex justify-content-center p-4">
				<ProgressSpinner />
			</div>

			<div v-else-if="!authenticated" class="flex gap-2 justify-around">
				<Button
					label="Re-authenticate"
					icon="pi pi-sign-in"
					@click="reAuthenticate"
					:loading="authenticating"
					class="w-full"
					severity="primary"
					style="width: 12rem"
				/>

				<Button label="Log Out" icon="pi pi-external-link" @click="goToLogin" class="w-full" severity="primary" style="width: 12rem" />
			</div>

			<div v-else class="flex flex-column gap-3">
				<div class="flex align-items-center gap-2 text-green-600">
					<i class="pi pi-check-circle"></i>
					<span>Successfully re-authenticated!</span>
				</div>
				<Button label="Continue" icon="pi pi-arrow-right" @click="closeModal" class="w-full" severity="success" />
			</div>

			<div v-if="error" class="p-message p-message-error">
				<i class="pi pi-exclamation-triangle mr-2"></i>
				{{ error }}
			</div>
		</div>
	</Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import Dialog from "primevue/dialog"
import Button from "primevue/button"
import ProgressSpinner from "primevue/progressspinner"
import { oktaAuth } from "../auth/oktaAuth"
import { appAuth } from "../auth/appAuth"
import { EventBus } from "@/eventBus"

const visible = ref(false)
const loading = ref(false)
const authenticating = ref(false)
const authenticated = ref(false)
const error = ref("")

// Listen for re-auth requests
EventBus.on("show-reauth-modal", () => {
	visible.value = true
	authenticated.value = false
	error.value = ""
})

const reAuthenticate = async () => {
	authenticating.value = true
	error.value = ""

	try {
		// Try to get a new token without prompting (if session still valid)
		const token = await oktaAuth.tokenManager.renew("accessToken")

		if (token) {
			// Token renewed successfully
			localStorage.setItem("token", token.accessToken)
			authenticated.value = true
			retryPendingCalls()
		} else {
			// Need to re-authenticate with popup
			await reAuthenticateWithPopup()
		}
	} catch (err) {
		// Session expired, need full re-authentication
		await reAuthenticateWithPopup()
	} finally {
		authenticating.value = false
	}
}

const reAuthenticateWithPopup = async () => {
	try {
		// Open a popup for re-authentication
		const tokenResponse = await oktaAuth.token.getWithPopup({
			responseType: ["token", "id_token"],
		})

		if (tokenResponse.tokens.accessToken) {
			// Store the new token
			localStorage.setItem("token", tokenResponse.tokens.accessToken.accessToken)
			oktaAuth.tokenManager.setTokens(tokenResponse.tokens)

			// Update session data
			await appAuth.loginToAppWithOktaToken(tokenResponse.tokens.accessToken.accessToken)

			authenticated.value = true
			retryPendingCalls()
		}
	} catch (err: any) {
		error.value = err.message || "Failed to re-authenticate. Please try logging in again."
		console.error("Re-authentication failed:", err)
	}
}

const retryPendingCalls = () => {
	// Signal that re-authentication is complete
	setTimeout(() => {
		EventBus.emit("reauth-completed")
		closeModal()
	}, 500)
}

const goToLogin = () => {
	// Clear storage and redirect to login
	sessionStorage.clear()
	localStorage.removeItem("token")
	localStorage.removeItem("PersonSessionData")
	oktaAuth.tokenManager.clear()
	window.location.href = "/login"
}

const closeModal = () => {
	visible.value = false
	authenticated.value = false
	error.value = ""
}
</script>

<style scoped>
.p-dialog-maximized {
	z-index: 9999 !important;
}

.p-message {
	padding: 0.75rem;
	border-radius: 6px;
	margin-top: 0.5rem;
}

.p-message-error {
	background: #fef2f2;
	border: 1px solid #fecaca;
	color: #dc2626;
}
</style>
